import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import moment from "moment";

//Components
import { Modal, ModalBody, ModalFooter, Button, Select, Form, ToggleSwitch } from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';
import { DatePicker } from "@gpstrackit/gtc-react-controls";

//Actions
import {
    createEditScheduledActionRequest,
    createEditScheduledActionGetDetailRequest,
    clearRedux,
} from 'Redux/actions';

//Helpers
import { getStatusOptions } from "Core/data/Helpers";

const EditScheduledAction = (props) => {
    const { tableRef } = props;
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const formRef = useRef(null);

    let statusLabels = [
        {
            id: 1,
            label: "Active"
        },
        {
            id: 2,
            label: "Voluntary Suspension"
        },
        {
            id: 3,
            label: "Involuntary Suspesion"
        },
        {
            id: 4,
            label: "Cancelled"
        }
    ]

    //Redux States
    const { openModal, loading, result, id, detail } = useSelector(state => state.editScheduledActionRedux);

    //Local States
    const [scheduleOn, setScheduleOn] = useState(null);
    const [entityName, setEntityName] = useState(null);
    const [status, setStatus] = useState(null);
    const [showToggleSwitch, setShowToggleSwitch] = useState(false);
    const [showWeb, setShowWeb] = useState(true);

    const closeModal = () => {
        dispatch(clearRedux("CREATE_EDIT_SCHEDULED"));
        setScheduleOn(null);
        setEntityName(null);
        setStatus(null);
        setShowToggleSwitch(false);
    }

    const applyChanges = () => {
        let newStatus = statusLabels.find((item) => item.id === status?.id)
        let data = { newStatusId: status?.id, newStatusName: newStatus.label }

        if (showToggleSwitch) {
            data.show = showWeb
        }

        let payload = {
            id: id,
            data: JSON.stringify(data),
            scheduleOn: moment(scheduleOn).format('YYYY-MM-DD'),
            entityId: detail?.entityId,
            entityName: detail?.entityName
        }
        dispatch(createEditScheduledActionRequest(payload));
    }

    useEffect(() => {
        if (openModal && id) {
            dispatch(createEditScheduledActionGetDetailRequest(id));
        }
    }, [id])

    useEffect(() => {
        if (detail?.id) {
            setEntityName(detail?.entityName)
            setScheduleOn(moment(detail.scheduleOn).utc().add(15, 'hours'))
            if (detail.data) {
                let data = JSON.parse(detail.data)

                if (data.newStatusId) {
                    let currentStatus = getStatusOptions(messages).find((item) => item.id === (data.newStatusId))
                    if (currentStatus?.id) setStatus(currentStatus)
                }

                if (detail.entityName === "Unit") {
                    setShowWeb(data?.show)
                    setShowToggleSwitch(true);
                }
            }
        }
    }, [detail])

    useEffect(() => {
        if (result?.id) {
            NotificationManager.success(
                messages['edit_scheduled_action_success_message'],
                messages['edit_scheduled_action_title'],
                3000
            );
            tableRef && tableRef?.current?.getData();
            closeModal();
        } else if (result?.error) {
            let message = messages['edit_scheduled_action_error_message']
            if(result?.error.includes('already has an scheduled')){
                message = messages['edit_scheduled_already_scheduled_action']
            }
            NotificationManager.error(
                message,
                messages['edit_scheduled_action_title'],
                3000
            );
            dispatch(clearRedux("CREATE_EDIT_SCHEDULED", { result: null }));
        }
    }, [result])

    return (
        <Modal
            open={openModal}
            title={messages['edit_scheduled_action_title']}
            handleClose={() => closeModal()}
            position="right"
            loading={loading}
            size="small"
        >
            <ModalBody>
                <div style={{ padding: '0 10px' }}>
                    <Form
                        ref={formRef}
                        onValidSubmit={() => applyChanges()}
                    >
                        <Select
                            id="change-status-id"
                            valueField="id"
                            textField="label"
                            value={status}
                            label={messages['edit_scheduled_status']}
                            placeholder={messages['edit_scheduled_status']}
                            options={entityName === "Account" ? getStatusOptions(messages).filter((item) => item.label !== "RMA") : getStatusOptions(messages)}
                            onChange={(value) => setStatus(value)}
                            required
                            requiredMessage={messages['required_field']}
                        />
                        {
                            showToggleSwitch ? (
                                <div style={{ marginTop: 12, width: '100%', display: 'flex', alignItems: 'center' }}>
                                    <label style={{ marginRight: 7, marginTop: 1 }} className="input-normal-label">{messages['feature_tab_show_in_web']} </label>
                                    <ToggleSwitch
                                        id="change-status-show-web"
                                        blueToggle
                                        large
                                        optionLabels={[messages['create_account_yes'], messages["create_account_not"]]}
                                        checked={showWeb}
                                        onChange={(checked) => setShowWeb(checked)}
                                        width={100}
                                    />
                                </div>
                            ) : <></>
                        }

                        <div style={{ marginTop: 10 }}>
                            <label style={{ marginRight: 5 }} className="input-normal-label">{messages['edit_scheduled_scheduled_at']}</label>
                            <DatePicker
                                calendarPosition="bottom"
                                format="MMMM DD, yyyy"
                                minDate={moment.now()}
                                onChange={(date) => setScheduleOn(date)}
                                placeholder={messages['feature_tab_select_date']}
                                showButtons
                                style={{ height: '40px' }}
                                timeZone={0}
                                value={scheduleOn}
                            />
                        </div>
                    </Form>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    className="btn btn-cancel"
                    onClick={() => closeModal()}
                >
                    {messages['common_cancel']}
                </Button>
                <Button
                    className="btn-dark-blue"
                    onClick={() => applyChanges()}
                >
                    {messages['common_save']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default EditScheduledAction;